/**
 * DynamicLoader - Animated loader with phases, timeout states, and accessibility
 */

import React, { useState, useEffect } from 'react';
import { useLoading } from '../contexts/LoadingContext';

const DynamicLoader = ({ 
  taskId,
  variant = 'modal', // 'modal' | 'inline'
  onRetry,
  className = ''
}) => {
  const { tasks, cancelTask } = useLoading();
  const task = tasks[taskId];
  
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTimeout, setShowTimeout] = useState(false);
  const [showExtendedTimeout, setShowExtendedTimeout] = useState(false);
  
  useEffect(() => {
    if (!task || task.status !== 'running') return;
    
    const startTime = task.startedAt;
    const interval = setInterval(() => {
      const elapsed = performance.now() - startTime;
      setElapsedTime(elapsed);
      
      if (elapsed > 10000 && !showTimeout) {
        setShowTimeout(true);
      }
      if (elapsed > 20000 && !showExtendedTimeout) {
        setShowExtendedTimeout(true);
      }
    }, 500);
    
    return () => clearInterval(interval);
  }, [task, showTimeout, showExtendedTimeout]);
  
  if (!task) return null;
  
  const currentPhase = task.phases[task.currentPhaseIndex];
  const isComplete = task.status === 'complete';
  const isFailed = task.status === 'failed';
  const isCancelled = task.status === 'cancelled';
  
  // Format elapsed time
  const formatTime = (ms) => {
    return (ms / 1000).toFixed(1) + 's';
  };
  
  // Inline variant (for buttons)
  if (variant === 'inline') {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        {task.status === 'running' && (
          <>
            <Spinner size="sm" />
            <span className="text-sm text-gray-600 animate-pulse">
              {currentPhase || task.label}
              <AnimatedEllipsis />
            </span>
          </>
        )}
        {isComplete && (
          <span className="text-sm text-green-600 font-semibold">
            ✅ Done
          </span>
        )}
        {isFailed && (
          <span className="text-sm text-red-600 font-semibold">
            ❌ Failed
          </span>
        )}
      </div>
    );
  }
  
  // Modal variant (full overlay)
  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="loader-title"
      aria-live={showExtendedTimeout ? 'assertive' : 'polite'}
    >
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-scale-in">
        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {task.status === 'running' && <Spinner size="lg" />}
          {isComplete && <div className="text-6xl">✅</div>}
          {isFailed && <div className="text-6xl">❌</div>}
          {isCancelled && <div className="text-6xl">⏹️</div>}
        </div>
        
        {/* Title */}
        <h3 
          id="loader-title" 
          className="text-xl font-bold text-center text-gray-800 mb-2"
        >
          {isComplete ? 'Complete!' : 
           isFailed ? 'Failed' :
           isCancelled ? 'Cancelled' :
           task.label}
        </h3>
        
        {/* Phase text */}
        {task.status === 'running' && currentPhase && (
          <p 
            className="text-center text-gray-600 mb-4"
            aria-live="polite"
          >
            {currentPhase}
            <AnimatedEllipsis />
          </p>
        )}
        
        {/* Error message */}
        {isFailed && task.error && (
          <p className="text-center text-red-600 text-sm mb-4">
            {task.error}
          </p>
        )}
        
        {/* Phase progress bar */}
        {task.status === 'running' && task.phases.length > 1 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Step {task.currentPhaseIndex + 1} of {task.phases.length}</span>
              <span>{formatTime(elapsedTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-blue-600 h-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${((task.currentPhaseIndex + 1) / task.phases.length) * 100}%` 
                }}
                role="progressbar"
                aria-valuenow={task.currentPhaseIndex + 1}
                aria-valuemin={1}
                aria-valuemax={task.phases.length}
              />
            </div>
          </div>
        )}
        
        {/* Timeout warnings */}
        {showTimeout && !showExtendedTimeout && task.status === 'running' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800 text-center">
              ⏱️ Still working...
            </p>
          </div>
        )}
        
        {showExtendedTimeout && task.status === 'running' && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-orange-800 text-center">
              ⚠️ Taking longer than usual... You can wait, retry, or cancel.
            </p>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-3 mt-6">
          {task.status === 'running' && task.canCancel && showExtendedTimeout && (
            <button
              onClick={() => cancelTask(taskId)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          )}
          
          {isFailed && onRetry && (
            <button
              onClick={onRetry}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              🔄 Retry
            </button>
          )}
          
          {(isComplete || isCancelled) && (
            <button
              onClick={() => {/* Parent will handle close */}}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Animated spinner component
const Spinner = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-16 h-16 border-4'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} border-blue-600 border-t-transparent rounded-full animate-spin`}
      role="status"
      aria-label="Loading"
    />
  );
};

// Animated ellipsis (...)
const AnimatedEllipsis = () => {
  const [dots, setDots] = useState(1);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev % 3) + 1);
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  return <span className="inline-block w-8">{'.'.repeat(dots)}</span>;
};

// CSS animations (add to global styles)
const styles = `
@keyframes scale-in {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale-in {
  animation: scale-in 0.2s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .animate-spin,
  .animate-pulse,
  .animate-scale-in {
    animation: none;
  }
}
`;

export default DynamicLoader;
